import { useMemo } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import type { U2104OrganizationPersonalizationHandler } from "./U2104OrganizationPersonalizationHandlers";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import { ORGANIZATION_PERSONALIZATION_NAME_TYPES } from "../../../common/organizationPersonalizationTypes";
import { useTranslation } from "react-i18next";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { SysOrgPersonalizationNameType, U2104_TIME_SESSION_ROUNDING_OPTIONS } from "../../../../U2104Service";

interface U2104OrganizationPersonalizationDlgProps {
  u2104OrganizationPersonalizationHandler: U2104OrganizationPersonalizationHandler;
  t: TFunction;
  NS: string;
}

function U2104OrganizationPersonalizationEditDlg({ u2104OrganizationPersonalizationHandler, t, NS }: U2104OrganizationPersonalizationDlgProps) {
  const { formStateHandlers } = u2104OrganizationPersonalizationHandler;
  const { i18n } = useTranslation();

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      u2104OrganizationPersonalizationHandler.onDlgClose("cancel");

      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      u2104OrganizationPersonalizationHandler.onDlgClose("save");
    }
  };

  const isEditMode = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
  const isDialogOpen = u2104OrganizationPersonalizationHandler.isDialogOpen;
  const selectedPersonalizationName = formStateHandlers.getData()?.personalizationName ?? "";
  const isTimeSessionRoundingType =
    selectedPersonalizationName === SysOrgPersonalizationNameType.ORG_TIME_SESSION_ROUNDING_MINUTES;

  const existingPersonalizations = u2104OrganizationPersonalizationHandler.gridStateHandlers.getData();
  const existingPersonalizationNames = new Set(existingPersonalizations.map((p) => p.personalizationName));

  const availableTypes = isEditMode
    ? ORGANIZATION_PERSONALIZATION_NAME_TYPES
    : ORGANIZATION_PERSONALIZATION_NAME_TYPES.filter((type) => !existingPersonalizationNames.has(type.value));

  const formattedData = availableTypes.map((type) => ({
    value: type.value,
    label: typeof type.label === "object" ? (i18n.language === "fr" ? type.label.fr : type.label.en) : String(type.label),
  }));

  const timeSessionRoundingOptions = useMemo(
    () =>
      U2104_TIME_SESSION_ROUNDING_OPTIONS.map((minutes) => ({
        value: String(minutes),
        label: t("lblTimeSessionRoundingMinutes", { ns: NS, count: minutes }),
      })),
    [t, NS],
  );

  const valuePlaceholder =
    selectedPersonalizationName === SysOrgPersonalizationNameType.ORG_TIMEZONE
      ? t("lblPersonalizationOrgTimezoneHint", { ns: NS })
      : undefined;

  const renderValueField = () => {
    if (isTimeSessionRoundingType) {
      return (
        <U2000ComboBox
          formStateHandlers={formStateHandlers}
          fieldName="personalizationValue"
          label={t("lblPersonalizationValue", { ns: NS })}
          data={timeSessionRoundingOptions}
          keyField="value"
          descField="label"
          required={true}
          placeholder={t("lblSelectTimeSessionRounding", { ns: NS })}
          getValue={(item) => item.value}
          maxHeight={120}
          findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
        />
      );
    }

    return (
      <U2000InputText
        fieldName="personalizationValue"
        label={t("lblPersonalizationValue", { ns: NS })}
        formStateHandlers={formStateHandlers}
        required={true}
        placeholder={valuePlaceholder}
      />
    );
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      title={t("tabPersonalization", { ns: NS })}
      isValid={true}
      height="h-[450px]"
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBox
          formStateHandlers={formStateHandlers}
          fieldName="personalizationName"
          label={t("lblPersonalizationName", { ns: NS })}
          data={formattedData}
          keyField="value"
          descField="label"
          required={true}
          placeholder={t("lblSelectPersonalizationName", { ns: NS })}
          readonly={isEditMode}
          getValue={(item) => item.value}
          maxHeight={200}
          findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
        />
        {renderValueField()}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2104OrganizationPersonalizationEditDlg;
